import * as Yup from "yup";

/**
 * Validation schema rule for report note
 */
export const ReportNotesSchema = Yup.object().shape({
  reportNotes: Yup.array().of(
    Yup.object().shape({
      note: Yup.string().required(),
      placeholders: Yup.array(),
      content: Yup.array().of(
        Yup.object().shape({
          description: Yup.string()
            .label("Note")
            .test(
              "is-custom-notes-empty",
              "The note should not be empty.",
              function (value) {
                const note = this.options?.from?.[1]?.value?.note;

                if (note === "Custom Notes") {
                  return !!value;
                } else {
                  return true;
                }
              }
            ),
          alert: Yup.string(),
          pageNumber: Yup.number().nullable(),
        })
      ),
    })
  ),
});

/**
 * Returns true if the value is a combination of characters between "custom" and "notes" or "note", false otherwise.
 *
 * @param {string} value
 * @returns {boolean}
 */
const isCustomNotes = (value) => /^custom.*(?:notes|note)$/i.test(value);

/**
 * Validation schema rule for report note section
 */
export const ReportNoteSectionSchema = Yup.object().shape({
  note: Yup.string()
    .required("Note is required.")
    .trim()
    .max(50, "Note can have maximum 50 characters.")
    .test(
      "is-custom-notes",
      "This note is not allowed.",
      (value) => !isCustomNotes(value)
    ),
  account: Yup.string().trim(),
  associated_note: Yup.string().nullable(),
  placeholders: Yup.array()
    .min(1, "At least one placeholder is required.")
    .required("Placeholders is/are required."),
});

/**
 * Validation schema rule for report note template
 */
export const ReportNoteTemplateSchema = Yup.object()
  .shape({
    billingCode: Yup.string()
      .trim()
      .when("account", {
        is: (account) => !!account,
        then: (schema) =>
          schema.when("device_group_name", {
            is: (device_group_name) => !device_group_name,
            then: (schema) =>
              schema.required(
                "Billing code is required when account is provided and device group is not provided."
              ),
            otherwise: (schema) => schema,
          }),
        otherwise: (schema) => schema.required("Billing code is required."),
      }),
    device_group_name: Yup.string()
      .trim()
      .label("Device group")
      .nullable()
      .when("account", {
        is: (account) => !!account,
        then: (schema) =>
          schema.when("billingCode", {
            is: (billingCode) => !billingCode,
            then: (schema) =>
              schema.required(
                "Device group is required when account is provided and billing code is not provided."
              ),
            otherwise: (schema) => schema.optional(),
          }),
        otherwise: (schema) => schema.optional(),
      }),
    account: Yup.string().trim(),
    sections: Yup.array()
      .of(
        Yup.object().shape({
          _id: Yup.string(),
          note: Yup.string().trim(),
          placeholders: Yup.array(),
        })
      )
      .min(1, "At least one section is required.")
      .required("Sections is required."),
  })
  .test(
    "account-billing-device-validation",
    "When account is provided, either billing code or device group is required.",
    function (values) {
      const { account, billingCode, device_group_name } = values;
      if (account && !billingCode && !device_group_name) {
        return this.createError({
          path: "billingCode",
          message:
            "Either billing code or device group is required when account is provided.",
        });
      }
      return true;
    }
  );
